<template>
  <div
    v-if="isActive && shouldBeVisibleComputed"
    id="banner"
    ref="bannerRef"
    v-editable="blok"
  >
    <transition name="SlideUpFade">
      <div
        v-if="isOpen"
        v-observe-visibility="visibilityObserver"
        class="relative z-50 flex w-full items-center text-base leading-5"
        :class="[
          {
            'text-white': textColor === 'white',
            'text-primary': textColor === 'black',
          },
        ]"
        :style="{
          'background-color': `${backgroundColor}`,
        }"
      >
        <section
          class="ml-5 mr-12 flex w-full flex-col items-center justify-center text-center sm:relative sm:flex-row"
          :class="{ 'sm:flex-col': hasScrollableLinks }"
        >
          <slot name="body">
            <div
              class="flex w-full flex-col items-center justify-center gap-1 py-2 md:flex-row md:gap-5"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="content" />
              <Countdown
                v-if="
                  hasCountdown && blok.counter_is_active && blok.countdown_until
                "
                :until="blok.countdown_until"
              />
              <ScrollableLinkList
                v-if="hasScrollableLinks"
                :links="blok.links"
                @click:promotion-click="onPromotionClick"
              />
            </div>
          </slot>
        </section>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import type { SbBanner } from '~/cms/storyblok/types/storyblok.d.ts'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

type BannerProps = {
  background?: string
  blok?: SbBanner & { published_at: string }
}

const props = withDefaults(defineProps<BannerProps>(), {
  background: 'black',
  blok: () => ({}) as SbBanner & { published_at: string },
})

const { isOpen, shouldBeVisible } = useBanner()
const { content } = useRichtext(props.blok.body)
const bannerRef = ref<HTMLDivElement>()
const { bannerHeight } = useUiState()

const { trackPromotionSelect } = useTrackingEvents()
const observerOptions = {
  intersection: { threshold: 0.5 },
  callback: () => {},
}

// Observed rather than measured once on a debounce: the PLP side navigation
// sizes itself against the chrome above it, so it needs the height right away
// on first paint and again every time the promo text rewraps to another line
// (ECOM-2796).
// `border-box` to match the `offsetHeight` being read: the content box would
// stay put through a padding or border change and leave the height stale.
useResizeObserver(
  bannerRef,
  () => {
    bannerHeight.value = bannerRef.value?.offsetHeight || 0
  },
  { box: 'border-box' },
)

const isActive = computed(() =>
  isEmpty(props.blok) ? true : props.blok.is_active,
)
const shouldBeVisibleComputed = computed(() =>
  shouldBeVisible(props.blok.published_at),
)
const hasCountdown = computed(
  () => !isEmpty(props.blok) && props.blok.countdown_until !== '',
)
const hasScrollableLinks = computed(
  () => !isEmpty(props.blok) && !isEmpty(props.blok.links),
)

const visibilityObserver = props.blok.promotion_id ? observerOptions : null

const onPromotionClick = () => {
  trackPromotionSelect({
    id: props.blok.promotion_id || 'banner_promotion_id',
    name: props.blok.promotion_name || 'banner_promotion_name',
    pageType: 'all',
    promoType: 'headerbanner',
    position: 1,
    creativeNameSb: props.blok.creative_name || '',
  })
}

const backgroundColor = computed(
  () => props.blok.background_color?.color || '#000000',
)

const textColor = computed(() => props.blok.text_color || 'white')
</script>
